import React from "react";
import { Box, Typography, Select, MenuItem, Chip } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { CustomDateCalendar, CustomTimePicker } from "../styles";
import { REPEAT_OPTIONS, ACTIVE_DATE_TYPES } from "../constants";

const DateScheduler = ({
  repeatOption,
  activeDate,
  fromDate,
  toDate,
  selectedTime,
  minToDate,
  onRepeatChange,
  onActiveDateChange,
  onDateChange,
  onTimeChange,
  language,
  tr,
}) => {
  const hasRepeat = repeatOption !== REPEAT_OPTIONS.NO_REPEAT;

  return (
    <LocalizationProvider
      dateAdapter={AdapterMoment}
      key={language}
      adapterLocale={language || "en"}>

      {/* Time + Repeat row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "10px",
          padding: "14px 20px 10px",
        }}>
        {/* Time picker */}
        <Box>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#42C0C7", mb: 0.5, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            {tr("postPage.time")}
          </Typography>
          <Box
            sx={{
              height: "40px",
              backgroundColor: "transparent",
              border: "1px solid rgba(66,192,199,0.5)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 1,
            }}>
            <CustomTimePicker
              value={selectedTime}
              onChange={onTimeChange}
              ampm={false}
              slotProps={{
                textField: {
                  variant: "standard",
                  InputProps: { disableUnderline: true },
                  sx: { "& input": { color: "var(--text-color)", fontSize: "14px", textAlign: "center" } },
                },
              }}
            />
          </Box>
        </Box>

        {/* Repeat select */}
        <Box>
          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#42C0C7", mb: 0.5, letterSpacing: "0.05em", textTransform: "uppercase" }}>
            {tr("postPage.withoutRepeat")}
          </Typography>
          <Select
            variant="outlined"
            fullWidth
            size="small"
            sx={{
              "height": "40px",
              "backgroundColor": "transparent",
              "color": "var(--text-color)",
              "borderRadius": "8px",
              "fontSize": "13px",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(66,192,199,0.5)" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#42C0C7" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#42C0C7" },
              "& .MuiSvgIcon-root": { color: "#42C0C7" },
            }}
            value={repeatOption}
            onChange={onRepeatChange}>
            <MenuItem value={REPEAT_OPTIONS.NO_REPEAT} sx={{ fontSize: "13px" }}>{tr("postPage.withoutRepeat")}</MenuItem>
            <MenuItem value={REPEAT_OPTIONS.ONCE} sx={{ fontSize: "13px" }}>{tr("postPage.once")}</MenuItem>
            <MenuItem value={REPEAT_OPTIONS.EVERY_DAY} sx={{ fontSize: "13px" }}>{tr("postPage.everyDay")}</MenuItem>
            <MenuItem value={REPEAT_OPTIONS.EVERY_WEEK} sx={{ fontSize: "13px" }}>{tr("postPage.everyWeek")}</MenuItem>
            <MenuItem value={REPEAT_OPTIONS.EVERY_MONTH} sx={{ fontSize: "13px" }}>{tr("postPage.everyMonth")}</MenuItem>
            <MenuItem value={REPEAT_OPTIONS.EVERY_YEAR} sx={{ fontSize: "13px" }}>{tr("postPage.everyYear")}</MenuItem>
          </Select>
        </Box>
      </Box>

      {/* From / To chips when repeat is on */}
      {hasRepeat && (
        <Box sx={{ display: "flex", gap: "8px", padding: "0 20px 10px" }}>
          <Chip
            label={fromDate ? fromDate.format("D MMM") : tr("postPage.from")}
            onClick={() => onActiveDateChange(ACTIVE_DATE_TYPES.FROM)}
            sx={{
              flex: 1,
              backgroundColor: activeDate === ACTIVE_DATE_TYPES.FROM ? "#42C0C7" : "transparent",
              color: activeDate === ACTIVE_DATE_TYPES.FROM ? "#fff" : "var(--text-color)",
              border: "1px solid",
              borderColor: activeDate === ACTIVE_DATE_TYPES.FROM ? "#42C0C7" : "var(--border-color)",
              fontWeight: 600,
              fontSize: "12px",
              cursor: "pointer",
              height: "32px",
            }}
          />
          <Chip
            label={toDate ? toDate.format("D MMM") : tr("postPage.to")}
            onClick={() => onActiveDateChange(ACTIVE_DATE_TYPES.TO)}
            disabled={repeatOption === REPEAT_OPTIONS.ONCE}
            sx={{
              flex: 1,
              backgroundColor: activeDate === ACTIVE_DATE_TYPES.TO ? "#42C0C7" : "transparent",
              color: activeDate === ACTIVE_DATE_TYPES.TO ? "#fff" : "var(--text-color)",
              border: "1px solid",
              borderColor: activeDate === ACTIVE_DATE_TYPES.TO ? "#42C0C7" : "var(--border-color)",
              fontWeight: 600,
              fontSize: "12px",
              cursor: "pointer",
              height: "32px",
            }}
          />
        </Box>
      )}

      {/* Calendar */}
      <Box sx={{ "& .MuiDateCalendar-root": { width: "100%" } }}>
        <CustomDateCalendar
          disablePast
          value={activeDate === ACTIVE_DATE_TYPES.FROM ? fromDate : toDate}
          onChange={onDateChange}
          minDate={activeDate === ACTIVE_DATE_TYPES.TO ? minToDate : null}
        />
      </Box>

    </LocalizationProvider>
  );
};

export default DateScheduler;

